import type { ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import z from "zod";

import { ChangePasswordForm } from "@/components/change-password-form";
import { ExternalLink } from "@/components/external-link";
import { LogoutButton } from "@/components/logout-button";
import { icons } from "@/ui";
import {
  AppearanceSettings,
  BasicInformationListItems,
  MoreMenuTab,
  MoreTabPanel,
  NotificationSettings,
} from "./-components";

type SetSelectedTab = (selectedTab?: string) => void;

const MENU_ITEMS = [
  {
    tab: "basic-information",
    text: "My account",
    icon: <icons.UserCircleIcon className="size-5" />,
  },
  { tab: "notifications", text: "Notifications", icon: <icons.BellIcon className="size-5" /> },
  { tab: "appearance", text: "Appearance", icon: <icons.SwatchIcon className="size-5" /> },
  { tab: "about-eo", text: "About EO", icon: <icons.EOCareIcon className="size-4" /> },
] as const;

const More = () => {
  const navigate = useNavigate();
  const { tab: selectedTab } = Route.useSearch();

  const setSelectedTab: SetSelectedTab = (selectedTab) => {
    navigate({ to: "/more", search: selectedTab ? { tab: selectedTab } : undefined });
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col text-content">
      <div className="flex min-h-0 flex-1 flex-col gap-8 sm:flex-row">
        <aside className="flex w-full shrink-0 flex-col gap-6 text-base sm:w-[230px] sm:justify-between lg:w-[292px]">
          <div>
            <h2 className="mb-3 text-xl font-bold text-content sm:hidden">More</h2>

            <nav aria-label="Settings" className="flex flex-col sm:gap-1">
              {MENU_ITEMS.map(({ icon, tab, text }) => {
                return (
                  <MoreMenuTab
                    active={selectedTab === tab}
                    icon={icon}
                    key={tab}
                    onClick={() => {
                      return setSelectedTab(tab);
                    }}
                    text={text}
                  />
                );
              })}
            </nav>
          </div>

          <LogoutButton className={selectedTab ? "hidden sm:flex" : "mt-2 w-fit"} />
        </aside>

        <div aria-hidden="true" className="hidden w-px bg-line sm:block" />

        <div className="min-h-0 sm:grow sm:overflow-y-auto">
          {selectedTab ? Tabs[selectedTab]?.(setSelectedTab) : null}
        </div>
      </div>
    </div>
  );
};

const ABOUT_EO_LINKS = [
  { link: "https://www.eo.care/web/about", text: "About EO" },
  { link: "https://www.eo.care/web/terms-of-use", text: "Terms of Use" },
  { link: "https://www.eo.care/web/privacy-policy", text: "Privacy Policy" },
  { link: "https://www.eo.care/methodology", text: "Methodology" },
  { link: "https://www.eo.care/research", text: "Research" },
];

const Tabs: Record<string, (setSelectedTab: SetSelectedTab) => ReactNode> = {
  "basic-information": (setSelectedTab) => {
    return (
      <MoreTabPanel
        onBack={() => {
          return setSelectedTab(undefined);
        }}
        title="My account"
      >
        <BasicInformationListItems />
      </MoreTabPanel>
    );
  },
  "change-password": (setSelectedTab) => {
    return (
      <MoreTabPanel
        onBack={() => {
          return setSelectedTab("basic-information");
        }}
        title="Change password"
      >
        <div className="flex flex-col gap-4">
          <h2 className="hidden text-xl font-semibold sm:block">Change password</h2>
          <ChangePasswordForm />
        </div>
      </MoreTabPanel>
    );
  },
  notifications: (setSelectedTab) => {
    return (
      <MoreTabPanel
        onBack={() => {
          return setSelectedTab(undefined);
        }}
        title="Notifications"
      >
        <NotificationSettings />
      </MoreTabPanel>
    );
  },
  appearance: (setSelectedTab) => {
    return (
      <MoreTabPanel
        onBack={() => {
          return setSelectedTab(undefined);
        }}
        title="Appearance"
      >
        <AppearanceSettings />
      </MoreTabPanel>
    );
  },
  "about-eo": (setSelectedTab) => {
    const utmParams = { utm_medium: "about_eo" };

    return (
      <MoreTabPanel
        onBack={() => {
          return setSelectedTab(undefined);
        }}
        title="About EO"
      >
        <div className="flex flex-col gap-1 sm:gap-3">
          {ABOUT_EO_LINKS.map(({ link, text }) => {
            return <ExternalLink key={link} link={link} text={text} utmParams={utmParams} />;
          })}
        </div>
      </MoreTabPanel>
    );
  },
};

export const Route = createFileRoute("/_private/(faqs)/more/")({
  component: More,
  validateSearch: z.object({
    tab: z.string().optional(),
  }),
});
